import * as React from 'react';
import { cn } from '@/lib/utils';

export interface TransactionFlowProgressProps {
  steps: string[];
  activeStep: number;
  onStepClick?: (index: number) => void;
  className?: string;
}

/**
 * Horizontal transaction progress: primary_fixed track, primary fill.
 * Current step uses elevated surface + ghost primary ring (design system).
 */
export function TransactionFlowProgress({
  steps,
  activeStep,
  onStepClick,
  className,
}: TransactionFlowProgressProps) {
  const n = steps.length;
  const pct =
    n <= 1 ? 100 : Math.min(100, Math.max(0, (activeStep / (n - 1)) * 100));

  return (
    <div className={cn('w-full', className)}>
      <div
        className="h-2 w-full overflow-hidden rounded-md bg-[var(--primary-fixed)]"
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full rounded-md bg-[var(--primary-navy)] transition-[width] duration-300 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-4 flex flex-wrap justify-between gap-2">
        {steps.map((label, idx) => {
          const isActive = idx === activeStep;
          const isDone = idx < activeStep;
          const clickable = onStepClick && idx <= activeStep;
          return (
            <button
              key={label}
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onStepClick(idx)}
              className={cn(
                'max-w-[140px] flex-1 rounded-md px-2 py-2 text-center text-xs font-medium transition-colors sm:text-sm',
                isActive &&
                  'bg-[var(--surface-container-lowest)] text-[var(--on-surface)] ring-2 ring-[rgba(0,8,30,0.2)]',
                !isActive &&
                  isDone &&
                  'text-[var(--on-surface-variant)] hover:text-[var(--on-surface)]',
                !isActive &&
                  !isDone &&
                  'cursor-default text-[var(--on-surface-variant)] opacity-60',
                clickable &&
                  !isActive &&
                  'cursor-pointer hover:bg-[var(--surface-container-low)]'
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
